/**
 * Keyboard access for a `<div role="button" tabindex="0">`: a real `<button>`
 * activates on Enter and Space by itself, a div does not.
 *
 * Extracted (2026-09-25) from three hand-written copies of the same handler
 * (`knowledge-sidebar-list`, `link-tile-knowledge-object`,
 * `timeline-area.factory`) when the issue dialog's hidden-infos bar became the
 * fourth user — per the house "reuse-before-reimplement" rule.
 */
export class KeyboardActivationHelper {

    /**
     * A `keydown` handler that runs `activate` on Enter or Space and ignores
     * every other key. `preventDefault()` keeps Space from scrolling the page.
     * Auto-repeat from a held key is swallowed (default still prevented, no
     * second activation): one press, one action — deliberately stricter than a
     * native `<button>`, which repeats on a held Enter.
     * The original event is passed on unchanged — callers such as the timeline
     * color toggle read `event.currentTarget`.
     *
     * @example
     * ```ts
     * html`<div role="button" tabindex="0"
     *           @click=${this._toggle}
     *           @keydown=${KeyboardActivationHelper.onEnterOrSpace(this._toggle)}></div>`
     * ```
     */
    public static onEnterOrSpace(activate: (event: KeyboardEvent) => void): (event: KeyboardEvent) => void {
        return (event: KeyboardEvent): void => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            if (event.repeat) return;
            activate(event);
        };
    }
}
